// Version-guarded call cache. Server truth wins: a lower version never replaces a
// higher one, and events only trigger a refetch of the authoritative resource.
import type { Call, CallContextCard, EventEnvelope } from '@bcio-voice/contracts';
import type { ApiClient } from '../lib/api.ts';

export type CallMap = ReadonlyMap<string, Call>;

/** GET /calls/{id} adds the integration's pinned context card (API-08, CallWithContextSchema). History rows,
 * command receipts and engine snapshots never carry it. */
export type CallWithContext = Call & { context_card?: CallContextCard };

export function contextCardOf(call: Call | null | undefined): CallContextCard | null {
  const card = (call as CallWithContext | null | undefined)?.context_card;
  return card && typeof card === 'object' && typeof card.version === 'number' ? card : null;
}

/** The card has its own version and is never deleted, so a read without one (or with an older one) keeps the stored card. */
function keepContextCard(existing: Call | undefined, call: Call): Call {
  const kept = contextCardOf(existing);
  const incoming = contextCardOf(call);
  if (!kept || (incoming && incoming.version >= kept.version)) return call;
  return { ...call, context_card: kept } as CallWithContext;
}

function keepContactBinding(existing: Call | undefined, call: Call): Call {
  if (!existing) return call;
  const existingVer = existing.contact_version ?? null;
  const incomingVer = call.contact_version ?? null;
  if (existingVer !== null && (incomingVer === null || incomingVer < existingVer)) {
    return {
      ...call,
      contact_id: call.contact_id ?? existing.contact_id,
      contact_display_name: existing.contact_display_name ?? null,
      contact_version: existingVer,
      contact_source: existing.contact_source ?? null,
    };
  }
  return call;
}

export function upsertCall(map: CallMap, call: Call): CallMap {
  const existing = map.get(call.id);
  if (existing && existing.version > call.version) return map;
  call = keepContextCard(existing, call);
  call = keepContactBinding(existing, call);
  if (existing && existing.version === call.version && sameCall(existing, call)) return map;
  const next = new Map(map);
  next.set(call.id, call);
  if (call.contact_id && typeof call.contact_version === 'number') {
    for (const [id, other] of next) {
      if (id === call.id || other.contact_id !== call.contact_id) continue;
      if ((other.contact_version ?? 0) <= call.contact_version && other.contact_display_name !== call.contact_display_name) {
        next.set(id, {
          ...other,
          contact_display_name: call.contact_display_name ?? null,
          contact_version: call.contact_version,
          contact_source: call.contact_source ?? other.contact_source ?? null,
        });
      }
    }
  }
  return next;
}

/** A newer card for a stored call, whatever the call projection's own version or request order. */
export function upsertContextCard(map: CallMap, callId: string, card: CallContextCard | null | undefined): CallMap {
  const existing = map.get(callId);
  if (!existing || !card || typeof card.version !== 'number') return map;
  const kept = contextCardOf(existing);
  if (kept && kept.version >= card.version) return map;
  const next = new Map(map);
  next.set(callId, { ...existing, context_card: card } as CallWithContext);
  return next;
}

/** Updates the stored context card's disposition after a one-click write-back. */
export function applyCallDisposition(map: CallMap, callId: string, disposition: string, updatedCard?: CallContextCard | null): CallMap {
  if (updatedCard && typeof updatedCard.version === 'number') return upsertContextCard(map, callId, updatedCard);
  const existing = map.get(callId);
  const kept = contextCardOf(existing);
  if (!existing || !kept) return map;
  const nextCard: CallContextCard = {
    ...kept,
    ...(kept.who ? { who: { ...kept.who, disposition } } : {}),
    fields: kept.fields.map(f => f.label === 'Disposition' ? { ...f, value: disposition } : f),
  };
  const next = new Map(map);
  next.set(callId, { ...existing, context_card: nextCard } as CallWithContext);
  return next;
}

export function upsertCalls(map: CallMap, calls: readonly Call[]): CallMap {
  return calls.reduce<CallMap>((acc, call) => upsertCall(acc, call), map);
}

function sameCall(a: Call, b: Call): boolean {
  return a.state === b.state && a.outcome === b.outcome && a.allowed_actions.join() === b.allowed_actions.join() && a.recording_state === b.recording_state && a.transcription_state === b.transcription_state
    && a.contact_id === b.contact_id && a.contact_display_name === b.contact_display_name && a.contact_version === b.contact_version && a.from.display_name === b.from.display_name
    && a.voicemail_drop?.drop_id === b.voicemail_drop?.drop_id && a.voicemail_drop?.result === b.voicemail_drop?.result && a.voicemail_drop?.played_ms === b.voicemail_drop?.played_ms && a.voicemail_drop?.ended_at === b.voicemail_drop?.ended_at
    && a.transfer?.id === b.transfer?.id && a.transfer?.mode === b.transfer?.mode && a.transfer?.state === b.transfer?.state
    && contextCardOf(a)?.version === contextCardOf(b)?.version && contextCardOf(a)?.title === contextCardOf(b)?.title;
}

export type EventDecision =
  | { kind: 'refetch_call'; callId: string }
  | { kind: 'refetch_agents' }
  | { kind: 'refetch_device' }
  | { kind: 'refetch_config' }
  | { kind: 'ignore'; reason: 'stale' | 'unknown_type' };

/** Maps a durable event to the resource that must be reloaded (events carry hints, not state). */
export function decideEvent(map: CallMap, event: EventEnvelope): EventDecision {
  switch (event.aggregate.type) {
    case 'call': {
      // These sequences belong to media settling and to the context card's own version (API-08), not controller CAS.
      if (event.type === 'transcript.state_changed' || event.type === 'call.context_updated') return { kind: 'refetch_call', callId: event.aggregate.id };
      const known = map.get(event.aggregate.id);
      if (known && known.version >= event.aggregate.version) return { kind: 'ignore', reason: 'stale' };
      return { kind: 'refetch_call', callId: event.aggregate.id };
    }
    case 'recording': {
      // visibleEvents has already checked media ACLs. The hint is only an ID:
      // the normal authenticated call route still decides what may be read.
      const id = event.data?.call_id;
      return event.type.startsWith('recording.') && typeof id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
        ? { kind: 'refetch_call', callId: id } : { kind: 'ignore', reason: 'unknown_type' };
    }
    case 'agent':
      return { kind: 'refetch_agents' };
    case 'device':
      return { kind: 'refetch_device' };
    case 'capability':
      return { kind: 'refetch_config' };
    default:
      return { kind: 'ignore', reason: 'unknown_type' };
  }
}

/** Request provenance is local ordering, never a server/command version. All workspace
 * HTTP call projections (detail, history and command receipts) share this clock.
 * Older requests cannot roll back a same-core-version media projection. Engine
 * snapshots without HTTP provenance cannot replace that projection at equal version. */
export class CallProjectionOrder {
  private issued = 0;
  private readonly source = new WeakMap<object, number>();
  private readonly applied = new Map<string, number>();
  begin(): number { return ++this.issued; }
  mark<T>(body: T, request: number): T {
    const data = (body as { data?: unknown } | null)?.data;
    const rows = Array.isArray(data) ? data : [data, (data as { call?: unknown } | null)?.call];
    for (const row of rows) {
      if (row && typeof row === 'object' && 'recording_state' in row && 'transcription_state' in row && 'version' in row) this.source.set(row, request);
    }
    return body;
  }
  current(id: string, request: number): boolean { return request >= (this.applied.get(id) ?? 0); }
  removed(id: string, request: number): void { this.applied.set(id, request); }
  accepts(map: CallMap, call: Call): boolean {
    const existing = map.get(call.id), request = this.source.get(call);
    if (!existing && (request !== undefined ? !this.current(call.id, request) : this.applied.has(call.id))) return false;
    if (existing && existing.version > call.version) return false;
    return !(existing?.version === call.version && request !== undefined && !this.current(call.id, request));
  }
  upsert(map: CallMap, call: Call): CallMap {
    if (!this.accepts(map, call)) return map;
    const existing = map.get(call.id), request = this.source.get(call);
    if (existing?.version === call.version) {
      if (request === undefined && this.applied.has(call.id)) call = { ...call, recording_state: existing.recording_state, transcription_state: existing.transcription_state };
    }
    if (request !== undefined) this.applied.set(call.id, Math.max(request, this.applied.get(call.id) ?? 0));
    return upsertCall(map, call);
  }
  /** Keep the original client's auth, CSRF, permissions, errors and methods. */
  client(api: ApiClient): ApiClient {
    return new Proxy(api, { get: (target, key) => {
      if (key === 'request') return async <T,>(...args: Parameters<ApiClient['request']>): Promise<T> => {
        const request = this.begin();
        return this.mark(await target.request<T>(...args), request);
      };
      const value: unknown = Reflect.get(target, key);
      return typeof value === 'function' ? value.bind(target) : value;
    } });
  }
}

export const LIVE_STATES: ReadonlySet<Call['state']> = new Set(['created', 'ringing', 'active', 'held']);

export function isLive(call: Call): boolean {
  return LIVE_STATES.has(call.state);
}

/** The live calls this user takes part in, newest first. */
export function liveCallsFor(map: CallMap, userId: string | null): Call[] {
  return [...map.values()]
    .filter(c => isLive(c) && (!userId || c.participants.some(p => p.user_id === userId && !p.left_at)))
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
}

/**
 * An incoming offer for this user: ringing, not started by this user. The server
 * does not expose the initiator directly, so an internal call whose destination is
 * this user, or an inbound call, counts as incoming.
 */
export function isIncomingFor(call: Call, userId: string | null): boolean {
  if (!userId) return false;
  if (call.transfer?.state === 'ringing' && call.transfer.target_user_id === userId) {
    const p = call.participants.find(x => x.user_id === userId && !x.left_at);
    if (p && !p.joined_at) return true;
  }
  if (call.state !== 'ringing') return false;
  if (call.direction === 'inbound') return true;
  return call.direction === 'internal' && call.destination.kind === 'internal' && call.destination.user_id === userId;
}

export function sortHistory(calls: readonly Call[]): Call[] {
  return [...calls].sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id.localeCompare(a.id));
}
